import React, { useEffect, useState } from "react";
import { TrendingUp, ShieldCheck, XCircle, AlertTriangle } from "lucide-react";
import { toast } from "../ui/Toast";

// Admin Career oversight tab (mounted in AdminPerformancePage). Scope: org-wide
// list + the HR decision gate. APPROVE mutates the employee's StaffProfile
// (title/band) — so approve is a CONFIRMED action stating the consequence, and
// both approve/reject are offered ONLY on pending_hr (the backend 409s
// otherwise). Reject requires a reason (backend 422s without it).

const API_BASE = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const STATUS_STYLE = {
  pending_hr: "text-amber-400 bg-amber-500/10 border-amber-500/25",
  approved:   "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/25",
  rejected:   "text-red-400 bg-red-500/10 border-red-500/20",
};
const STATUS_FALLBACK = "text-white/30 bg-white/5 border-white/10";
const STATUS_LABEL = { pending_hr: "Pending HR", approved: "Approved", rejected: "Rejected" };
const EVENT_LABEL = { promotion: "Promotion", grade_change: "Grade Change", title_change: "Title Change", transfer: "Transfer" };
const FILTERS = ["ALL", "pending_hr", "approved", "rejected"];

const fmtDate = (d) => (d ? String(d).slice(0, 10) : "—");
const val = (v) => (v === null || v === undefined || v === "" ? "—" : v);
const statusBadge = (s) => (
  <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[s] || STATUS_FALLBACK}`}>{STATUS_LABEL[s] || s || "—"}</span>
);

// Explicit consequence statement for the approve confirmation.
const consequence = (e) => {
  const name = e.employee_name || "this employee";
  const parts = [];
  if (e.proposed_title) parts.push(`title to "${e.proposed_title}"`);
  if (e.proposed_grade) parts.push(`grade to "${e.proposed_grade}"`);
  if (parts.length === 0) return `Approving finalises this ${EVENT_LABEL[e.event_type] || e.event_type} for ${name}.`;
  return `Approving updates ${name}'s ${parts.join(" and ")} — this writes their HR record.`;
};

export default function CareerTab() {
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [filter, setFilter]   = useState("ALL");
  const [panel, setPanel]     = useState(null);     // { id, kind: 'approve'|'reject' }
  const [notes, setNotes]     = useState("");
  const [reason, setReason]   = useState("");
  const [busy, setBusy]       = useState(false);

  const token = localStorage.getItem("token");
  const authH = { Authorization: `Bearer ${token}` };
  const jsonH = { ...authH, "Content-Type": "application/json" };

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/performance/career-events`, { headers: authH });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setEvents(Array.isArray(data) ? data : []);
    } catch (e) { setError(e.message); setEvents([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const openPanel = (id, kind) => {
    if (panel && panel.id === id && panel.kind === kind) { setPanel(null); return; }
    setNotes(""); setReason(""); setPanel({ id, kind });
  };

  const doApprove = async (id) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/performance/career-events/${id}/approve`, {
        method: "PATCH", headers: jsonH, body: JSON.stringify({ notes: notes || null }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = await res.json();
      setEvents(es => es.map(e => (e.id === id ? { ...e, ...updated } : e)));
      setPanel(null);
      toast.success("Approved — employee's HR record updated & notified");
    } catch { toast.error("Failed to approve"); }
    finally { setBusy(false); }
  };

  const doReject = async (id) => {
    if (!reason.trim()) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/performance/career-events/${id}/reject`, {
        method: "PATCH", headers: jsonH, body: JSON.stringify({ reason }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = await res.json();
      setEvents(es => es.map(e => (e.id === id ? { ...e, ...updated } : e)));
      setPanel(null);
      toast.success("Rejected");
    } catch { toast.error("Failed to reject"); }
    finally { setBusy(false); }
  };

  // pending_hr first (they need the admin's decision), then the API's newest-first.
  const ordered = [...events]
    .filter(e => filter === "ALL" || e.status === filter)
    .sort((a, b) => (a.status === "pending_hr" ? 0 : 1) - (b.status === "pending_hr" ? 0 : 1));
  const pendingCount = events.filter(e => e.status === "pending_hr").length;

  return (
    <div className="space-y-5">
      {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">⚠ {error}</div>}

      <div className="flex gap-1.5 flex-wrap">
        {FILTERS.map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              filter === f
                ? "bg-[#00ff90]/15 border-[#00ff90]/40 text-[#00ff90]"
                : "bg-transparent border-white/10 text-white/40 hover:border-[#00ff90]/20 hover:text-white/60"
            }`}>
            {f === "ALL" ? "All" : STATUS_LABEL[f]}
            {f === "pending_hr" && pendingCount > 0 && <span className="ml-1 text-amber-400">({pendingCount})</span>}
          </button>
        ))}
      </div>

      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
          <TrendingUp size={14} className="text-[#00ff90]" />
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Career Events</span>
          {pendingCount > 0 && <span className="text-xs text-amber-400/70">· {pendingCount} awaiting decision</span>}
        </div>

        <div className="divide-y divide-white/5">
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => <div key={i} className="px-5 py-4"><div className="h-12 rounded-lg bg-white/5 animate-pulse" /></div>)
          ) : ordered.length === 0 ? (
            <p className="px-5 py-8 text-white/20 text-sm italic text-center">No career events{filter !== "ALL" ? ` with status "${STATUS_LABEL[filter]}"` : ""}.</p>
          ) : ordered.map(e => (
            <div key={e.id}>
              <div className="flex items-start justify-between gap-3 px-5 py-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <p className="text-sm text-white/80 font-medium">{e.employee_name || e.employee_id}</p>
                    <span className="text-xs text-white/30 border border-white/10 px-2 py-0.5 rounded-full">{EVENT_LABEL[e.event_type] || e.event_type}</span>
                    {statusBadge(e.status)}
                  </div>
                  <p className="text-xs text-white/40">
                    Title: {val(e.current_title)} → {val(e.proposed_title)} · Grade: {val(e.current_grade)} → {val(e.proposed_grade)}
                  </p>
                  <p className="text-xs text-white/25 mt-0.5">
                    Nominated by {e.nominated_by_name || e.nominated_by} · Effective {fmtDate(e.effective_date)}
                  </p>
                  {e.justification && <p className="text-xs text-white/40 mt-1">{e.justification}</p>}
                  {/* Decided rows show the decision + note instead of actions. */}
                  {(e.status === "approved" || e.status === "rejected") && e.hr_notes && (
                    <p className="text-xs text-white/50 mt-0.5">
                      <span className="text-white/30">{e.status === "rejected" ? "Reason: " : "HR note: "}</span>{e.hr_notes}
                    </p>
                  )}
                </div>
                <div className="flex gap-1.5 shrink-0 flex-wrap justify-end">
                  {/* Approve/reject offered ONLY on pending_hr (backend 409s otherwise). */}
                  {e.status === "pending_hr" && (
                    <>
                      <button onClick={() => openPanel(e.id, "approve")}
                        className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/30 bg-[#00ff90]/10 text-[#00ff90] hover:bg-[#00ff90]/20 transition-colors">
                        <ShieldCheck size={12} /> Approve
                      </button>
                      <button onClick={() => openPanel(e.id, "reject")}
                        className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-red-500/25 bg-red-500/5 text-red-300 hover:bg-red-500/15 transition-colors">
                        <XCircle size={12} /> Reject
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Confirmation panels */}
              {panel && panel.id === e.id && (
                <div className="mx-5 mb-4 rounded-xl border border-[#00ff90]/15 bg-[#00ff90]/3 p-4 space-y-3">
                  {panel.kind === "approve" && (
                    <>
                      <div className="flex items-start gap-2 rounded-lg border border-amber-500/25 bg-amber-500/5 p-3">
                        <AlertTriangle size={14} className="text-amber-400 shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-200/80">{consequence(e)}</p>
                      </div>
                      <div>
                        <label className={labelCls}>HR Notes (optional)</label>
                        <input className={inputCls} value={notes} onChange={ev => setNotes(ev.target.value)} />
                      </div>
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setPanel(null)} className="text-xs px-4 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
                        <button onClick={() => doApprove(e.id)} disabled={busy}
                          className="text-xs px-4 py-1.5 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors">
                          Confirm Approve
                        </button>
                      </div>
                    </>
                  )}
                  {panel.kind === "reject" && (
                    <>
                      <div>
                        <label className={labelCls}>Reason <span className="text-amber-400/60">* required</span></label>
                        <textarea className={inputCls} rows={2} value={reason} onChange={ev => setReason(ev.target.value)}
                          placeholder="Why is this being rejected?" />
                      </div>
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setPanel(null)} className="text-xs px-4 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
                        <button onClick={() => doReject(e.id)} disabled={busy || !reason.trim()}
                          className="text-xs px-4 py-1.5 rounded-lg bg-red-500/15 text-red-300 border border-red-500/30 hover:bg-red-500/25 disabled:opacity-40 transition-colors">
                          Confirm Reject
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
